import * as React from 'react';
import * as autoBind from 'react-autobind';
import {ControlButton, TscButtonType, TextInput} from '@techsmith/tsc-cloud-style-guide';
import SelectGroupsButton from '../SelectGroupsButton/SelectGroupsButton';
import Constants, {ModalStates} from '../../globals/Constants';
import TextSanitizer from '../../utils/TextSanitizer/TextSanitizer';
import Strings from '../../globals/Strings';
import ReviewerGroupModel from '../../types/ReviewerGroupModel';
import SimpleReviewerModel from '../../types/SimpleReviewerModel';
import Themeable from '../../types/Themeable';
import InlineInput from '../InlineInput/InlineInput';
import ReviewersListContainer from '../ReviewersList/ReviewersListContainer';
import SaveGroupButton from '../SaveGroupButton/SaveGroupButton';
import HelpLink from '../HelpLink/HelpLink';

const EMAIL_INPUT_DELIMITER = /;|,|\s+/;

export default class ManageReviewers extends React.Component<ManageReviewersProps, ManageReviewersState> {
   readonly state: ManageReviewersState = {input: '', badEmailsCount: 0, currentKeyCode: null};

   constructor(props: ManageReviewersProps) {
      super(props);
      autoBind(this);
   }

   onEnter(): void {
      if (this.state.input !== '') {
         this.clickAdd();
      }
   }

   onEscape(): void {
      this.setState({input: ''});
   }

   onEmailChange(event: any): void {
      this.setState({input: event.currentTarget.value, badEmailsCount: 0});
   }

   isEmailAlreadyListed(email: string): boolean {
      return this.props.reviewers.some(reviewer => reviewer.Email === email);
   }

   isAddDisabled(): boolean {
      const {input, badEmailsCount} = this.state;
      return badEmailsCount !== 0 || input === '';
   }

   clickAdd(): void {
      if (this.isAddDisabled()) {
         return;
      }

      const badReviewers: string[] = [];
      const goodReviewers: string[] = [];
      const inputReviewers = this.state.input.split(EMAIL_INPUT_DELIMITER).filter((value, index, self) => self.indexOf(value) === index);

      inputReviewers.forEach(email => {
         email = email.trim().toLowerCase();
         if (email && !TextSanitizer.isValidEmail(email)) {
            badReviewers.push(email);
         } else if (email) {
            goodReviewers.push(email);
         }
      });

      this.setState({input: badReviewers.join(', '), badEmailsCount: badReviewers.length});
      this.addEmails(goodReviewers);
   }

   addEmails(emails: string[]): void {
      this.props.addReviewers(emails.map(email => ({
         Email: email,
         Status: Constants.reviewerStatus.pending,
         Id: null,
         Notify: true
      })));
   }

   render(): React.ReactElement<any> {
      const {prompt, reviewers, isOwner, reviewerGroups, enableSaveGroup, modalState, showInput, cancelRemoveReviewer, removeReviewer, themeName, errorText, showRemoveReviewerText, notifyReviewer} = this.props;
      const {badEmailsCount, input} = this.state;
      const rejectedText = Strings.labels.manageReviewers.rejected.replace('{param0}', badEmailsCount + '').replace('{param1}', badEmailsCount === 1 ? '' : 's');
      const groupsButton = modalState !== ModalStates.manageReviewerGroups && <SelectGroupsButton themeName={themeName} addEmails={this.addEmails} disabled={!reviewerGroups.length}/>;
      const emailInput = <TextInput sharedId="manage-reviewers-input" onEnter={this.onEnter} onEscape={this.onEscape} themeName={themeName} placeholder={Strings.defaults.manageReviewersInput} onChange={this.onEmailChange} value={input} testId="manage-reviewers-input" errorText={errorText}/>;
      const addButton = <ControlButton themeName={themeName} buttonType={TscButtonType.secondary} label={Strings.labels.add} onClick={this.clickAdd} disabled={this.isAddDisabled()} testId="email-input-add-button" width="4.75rem"/>;

      return (
         <div className="manage-reviewers">
            {showInput && <div>
               {isOwner && prompt && <label htmlFor="manage-reviewers-input" className="input-label">{prompt}</label>}
                <div className="email-input-container">
                  {isOwner && <div className="email-input-field-group">
                     <InlineInput leftComponent={groupsButton} middleComponent={emailInput} rightComponent={addButton}/>
                  </div>}
                  <div className="total-rejected-label-placeholder">
                     {isOwner && badEmailsCount > 0 && <div className="total-rejected-label">{rejectedText}</div>}
                  </div>
               </div>
            </div>}
            <ReviewersListContainer cancelRemoveReviewer={cancelRemoveReviewer} removeReviewer={removeReviewer} reviewers={reviewers} isOwner={isOwner} notifyReviewer={notifyReviewer}/>
            {isOwner && enableSaveGroup && <SaveGroupButton themeName={themeName} reviewers={reviewers} reviewerGroups={reviewerGroups}/>}
            {showRemoveReviewerText && <div className="remove-reviewer-message">{Strings.labels.removeReviewerMessage} <HelpLink href={Constants.siteLinks.help.reviewers} /></div>}
         </div>
      );
   }
}

export interface ManageReviewersProps extends Themeable {
   prompt: string;
   modalState: ModalStates;
   reviewers: SimpleReviewerModel[];
   isOwner: boolean;
   reviewerGroups: ReviewerGroupModel[];
   enableSaveGroup: boolean;
   showInput: boolean;
   errorText?: string;
   showRemoveReviewerText: boolean;
   addReviewers(reviewers: SimpleReviewerModel[]): void;
   cancelRemoveReviewer?(reviewer: SimpleReviewerModel): void;
   removeReviewer(reviewer: SimpleReviewerModel): void;
   notifyReviewer(reviewer: SimpleReviewerModel, wantToNotify: boolean): void;
}

export interface ManageReviewersState {
   input: string;
   badEmailsCount: number;
   currentKeyCode: number;
}
